/* eslint-disable import/order */
/* eslint-disable camelcase */
import { sentenceCase } from 'change-case';
import { useState, useCallback, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Card,
  Table,
  Avatar,
  Button,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TableHead,
  Box,
  CircularProgress,
  IconButton,
} from '@mui/material';
import axios, { getErrorMessage } from 'utils/axios';
import useIsMountedRef from 'hooks/useIsMountedRef';
import CRUD from 'utils/Crud';
import { fDateTime } from 'utils/formatTime';
import { useSnackbar } from 'notistack';
import useSettings from '../../hooks/useSettings';
import { useTheme } from '@mui/material/styles';
import Page from '../../components/Page';
import Label from '../../components/Label';
import Iconify from '../../components/Iconify';
import Scrollbar from '../../components/Scrollbar';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import { LoadingButton } from '@mui/lab';
import Swal from 'sweetalert2';
import AppColors from 'utils/AppColors';
import { handleSelectClick } from 'utils/commonFunc';
import AddCryptoModal from 'sections/@dashboard/crypto/AddCryptoModal';

export default function CryptoList() {
  const theme = useTheme();
  const { themeStretch } = useSettings();
  const isMountedRef = useIsMountedRef();
  const [modal, setModal] = useState({ open: false, user: {} });
  const [userList, setUserList] = useState([]);
  const [selected, setSelected] = useState([]);
  const [state, setState] = useState({ loading: false, loadComplete: false, error: null, isDeleting: false, isReordering: false });
  const { enqueueSnackbar } = useSnackbar();

  const getInitData = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, loadComplete: false, error: null }));
    try {
      const response = await axios.get(CRUD.cryptoAssets);
      if (isMountedRef.current) {
        // Server returns ordered by priority ascending
        setUserList(response.data.sort((a, b) => a.priority - b.priority));
      }
      setState((prev) => ({ ...prev, loading: false, loadComplete: true, error: null }));
    } catch (error) {
      setState((prev) => ({ ...prev, loading: false, loadComplete: false, error: getErrorMessage(error) }));
    }
  }, [isMountedRef]);

  useEffect(() => {
    getInitData();
  }, [getInitData]);


  // Move Asset in the List
  const handleReorder = async (index, direction) => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === userList.length - 1) return;

    const newList = [...userList];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    // Swap positions in array
    const temp = newList[index];
    newList[index] = newList[targetIndex];
    newList[targetIndex] = temp;

    // Update local priority numbers (assuming consecutive integers)
    const updatedList = newList.map((item, idx) => ({
      ...item,
      priority: idx + 1, // ensures consistent ordering
    }));

    setUserList(updatedList);
    setState((prev) => ({ ...prev, isReordering: true }));

    try {
      // Notify server with updated priorities
      await axios.put(`${CRUD.cryptoAssets}/priority`, {
        order: updatedList.map(({ coin_id, priority }) => ({ coin_id, priority })),
      });
      enqueueSnackbar('Order updated', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error) || 'Failed to update order', { variant: 'error' });
      // Rollback if server call fails
      getInitData();
    }

    setState((prev) => ({ ...prev, isReordering: false }));
  };

  return (
    <Page title="Manage Coins">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Cryptocurrencies"
          links={[
            { name: 'Dashboard', href: '/' },
            { name: 'Cryptocurrency' },
            { name: 'List' },
          ]}
          action={
            <Button
              sx={{ ml: 2 }}
              variant="contained"
              onClick={() => {
                setModal({ open: true, isEdit: false });
              }}
              startIcon={<Iconify icon={'eva:plus-fill'} />}
            >
              Add Asset
            </Button>
          }
        />

        {state.loading && (
          <Card>
            <Box padding={10} display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          </Card>
        )}

        {!state.loading && state.error && (
          <Card>
            <Box padding={10} display="flex" justifyContent="center">
              <p>Error {state.error}</p>
            </Box>
          </Card>
        )}

        {state.loadComplete && (
          <Card>
            <Scrollbar>
              <TableContainer sx={{ minWidth: 800 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{' '}</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Allow Sell</TableCell>
                      <TableCell>Allow Buy</TableCell>
                      <TableCell>Created at</TableCell>
                      <TableCell align="right">{'Actions'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userList.map((row, index) => {
                      const { id, name, image_url, coin_id, created_at, allow_sell, allow_buy } = row;
                      const isItemSelected = selected.indexOf(id) !== -1;

                      return (
                        <TableRow hover key={id} selected={isItemSelected} role="checkbox" aria-checked={isItemSelected}>
                          <TableCell padding="checkbox">
                            <Checkbox checked={isItemSelected} onClick={() => handleSelectClick(id, selected, setSelected)} />
                          </TableCell>
                          <TableCell sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar alt={name} src={image_url} sx={{ mr: 2 }} />
                            <Typography variant="subtitle2" noWrap>
                              {name} ({coin_id})
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Label variant={theme.palette.mode === 'light' ? 'ghost' : 'filled'} color={!allow_sell ? 'error' : 'success'}>
                              {allow_sell ? 'Enabled' : 'Disabled'}
                            </Label>
                          </TableCell>
                          <TableCell>
                            <Label variant={theme.palette.mode === 'light' ? 'ghost' : 'filled'} color={!allow_buy ? 'error' : 'success'}>
                              {allow_buy ? 'Enabled' : 'Disabled'}
                            </Label>
                          </TableCell>
                          <TableCell>{fDateTime(created_at)}</TableCell>
                          <TableCell align="right">
                            {/* Reorder Buttons */}
                            <IconButton size="small" color="primary" onClick={() => handleReorder(index, 'up')} disabled={index === 0 || state.isReordering}>
                              <Iconify icon="eva:arrow-up-fill" />
                            </IconButton>
                            <IconButton size="small" color="primary" onClick={() => handleReorder(index, 'down')} disabled={index === userList.length - 1 || state.isReordering}>
                              <Iconify icon="eva:arrow-down-fill" />
                            </IconButton>

                            {/* Edit and Delete */}
                            <Button
                              variant="outlined"
                              sx={{ ml: 1, mr: 1 }}
                              color="secondary"
                              onClick={() => setModal({ open: true, isEdit: true, coin: row })}
                            >
                              Edit
                            </Button>
                            <LoadingButton
                              type="submit"
                              onClick={() => removeUsers(row)}
                              variant="outlined"
                              color="error"
                              loading={state.isDeleting}
                            >
                              <Iconify icon={'eva:trash-2-outline'} sx={{ mr: 1, width: 20, height: 20 }} /> Delete
                            </LoadingButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
          </Card>
        )}
      </Container>

      {modal.open && (
        <AddCryptoModal
          open={modal.open}
          isEdit={modal.isEdit}
          coin={modal.coin}
          handleClose={() => setModal({ open: false })}
          handleComplete={() => {
            setModal({ open: false });
            getInitData();
          }}
        />
      )}
    </Page>
  );

  // Delete function
  function removeUsers(row) {
    Swal.fire({
      text: 'Do you want to remove this asset?',
      icon: 'warning',
      showCancelButton: true,
      // eslint-disable-next-line import/no-named-as-default-member
      confirmButtonColor: AppColors.DANGER,
      confirmButtonText: 'Yes, continue',
      reverseButtons: true,
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: async () => {
        try {
          await axios.delete(`${CRUD.cryptoAssets}/${row.coin_id}`);
          setUserList((prev) => prev.filter((e) => e.id !== row.id));
          return true;
        } catch (error) {
          Swal.showValidationMessage(`${getErrorMessage(error) || error.toString()}`);
          return false;
        }
      },
    }).then((result) => {
      if (result.value) {
        Swal.close();
      }
    });
  }
}
